"use client";

import { useEffect } from "react";
import { signalNativeSplashReady as signalAndroidSplashReady } from "@/lib/native/hiive-splash";

type SplashWebkit = {
  messageHandlers?: {
    hiiveSplashReady?: { postMessage: (message: string) => void };
  };
};

function signalIosSplashReady() {
  try {
    const webkit = (window as unknown as { webkit?: SplashWebkit }).webkit;
    // Newer WebKit returns a promise that rejects with InvalidAccessError once
    // the native side has removed the handler (the splash is already gone),
    // which surfaced as an unhandled rejection on every launch.
    const sent: unknown =
      webkit?.messageHandlers?.hiiveSplashReady?.postMessage("ready");
    if (sent instanceof Promise) sent.catch(() => {});
  } catch {
    // Not running inside the iOS WebView.
  }
}

export function NativeSplashReady() {
  useEffect(() => {
    // Android releases now, not on a frame: its held system splash cancels
    // every draw of the content view, and the WebView only receives
    // BeginFrames from that draw pass, so `requestAnimationFrame` cannot fire
    // until the splash is already gone (see native-splash-early-signal).
    // Hydration means the content is laid out, which is all Android can know
    // before the release. No-ops on every other platform.
    void signalAndroidSplashReady();

    let cancelled = false;

    // iOS: release the splash on the first painted frame, without waiting for
    // the deep-link probe (see native-splash-gate). A cold start on a link
    // renders its start page first and navigates once the launch URL is read,
    // so the skeleton is briefly visible before the destination commits —
    // accepted deliberately: holding the logo over that window made every
    // launch, deep link or not, pay the probe's full cost.
    const frame = requestAnimationFrame(() => {
      if (!cancelled) {
        signalIosSplashReady();
      }
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, []);

  return null;
}
